import React, { useState, useEffect } from 'react';
import { User } from 'lucide-react';

interface DriverInfo {
  maritalStatus: string;
  isHomeOwner: string;
  livedInUKSinceBirth: string;
  employmentStatus: string;
  licenseType: string;
  licenseHeld: {
    years: string;
    months: string;
  };
  otherVehicles: string;
}

interface DriversInfoStepProps {
  data: DriverInfo;
  onInputChange: (field: string, value: string | { [key: string]: string }) => void;
}

export function DriversInfoStep({ data, onInputChange }: DriversInfoStepProps) {
  const [monthsVisible, setMonthsVisible] = useState(false);

  // Show months field only if years is 0
  useEffect(() => {
    setMonthsVisible(data.licenseHeld.years === '0');
  }, [data.licenseHeld.years]);

  return (
    <div className="space-y-4">
      <div className="flex items-center space-x-2 mb-6">
        <User className="w-6 h-6 text-indigo-600" />
        <h2 className="text-xl font-semibold">Driver Information</h2>
      </div>

      <div className="border-b pb-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">About You</h3>
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Marital Status
            </label>
            <select
              value={data.maritalStatus}
              onChange={(e) => onInputChange('maritalStatus', e.target.value)}
              className="w-full p-2 border rounded"
              required
            >
              <option value="">Select status</option>
              <option value="Single">Single</option>
              <option value="Married">Married</option>
              <option value="Living with partner">Living with partner/Common law</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Are you a home owner?
            </label>
            <div className="flex space-x-4">
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  value="Yes"
                  checked={data.isHomeOwner === 'Yes'}
                  onChange={(e) => onInputChange('isHomeOwner', e.target.value)}
                  className="rounded-full h-4 w-4 text-indigo-600"
                  required
                />
                <span className="ml-2">Yes</span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  value="No"
                  checked={data.isHomeOwner === 'No'}
                  onChange={(e) => onInputChange('isHomeOwner', e.target.value)}
                  className="rounded-full h-4 w-4 text-indigo-600"
                  required
                />
                <span className="ml-2">No</span>
              </label>
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Have you lived in the UK since birth?
            </label>
            <div className="flex space-x-4">
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  value="Yes"
                  checked={data.livedInUKSinceBirth === 'Yes'}
                  onChange={(e) => onInputChange('livedInUKSinceBirth', e.target.value)}
                  className="rounded-full h-4 w-4 text-indigo-600"
                  required
                />
                <span className="ml-2">Yes</span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  value="No"
                  checked={data.livedInUKSinceBirth === 'No'}
                  onChange={(e) => onInputChange('livedInUKSinceBirth', e.target.value)}
                  className="rounded-full h-4 w-4 text-indigo-600"
                  required
                />
                <span className="ml-2">No</span>
              </label>
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Employment Status
            </label>
            <select
              value={data.employmentStatus}
              onChange={(e) => onInputChange('employmentStatus', e.target.value)}
              className="w-full p-2 border rounded"
              required
            >
              <option value="">Select status</option>
              <option value="Employed">Employed</option>
              <option value="Self Employed">Self Employed</option>
              <option value="Full time education">Full time education</option>
              <option value="Unemployed">Unemployed</option>
              <option value="Not in employment due to disability">Not in employment due to disability</option>
              <option value="housewife/househusband">Housewife/Househusband</option>
              <option value="Retired">Retired</option>
            </select>
          </div>
        </div>
      </div>

      <div className="pt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Driving Information</h3>
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Type of Licence
            </label>
            <select
              value={data.licenseType}
              onChange={(e) => onInputChange('licenseType', e.target.value)}
              className="w-full p-2 border rounded"
              required
            >
              <option value="">Select licence type</option>
              <option value="Full (UK)">Full (UK)</option>
              <option value="Provisional (UK)">Provisional (UK)</option>
              <option value="Full (other)">Full (other)</option>
              <option value="Provisional (other)">Provisional (other)</option>
            </select>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              How long have you held your licence?
            </label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Years</label>
                <input
                  type="number"
                  value={data.licenseHeld.years}
                  onChange={(e) => {
                    const years = e.target.value;
                    const yearsNum = parseInt(years);
                    if (yearsNum < 0) return; // Prevent negative values
                    setMonthsVisible(yearsNum === 0);
                    onInputChange('licenseHeld', {
                      ...data.licenseHeld,
                      years,
                      // Clear months if years > 0 
                      months: yearsNum === 0 ? data.licenseHeld.months : ''
                    });
                  }}
                  className="w-full p-2 border rounded"
                  min="0" 
                  max="99"
                  onWheel={(e) => e.currentTarget.blur()}
                  required
                />
              </div>
              {monthsVisible && (
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Months</label>
                  <input
                    type="number"
                    value={data.licenseHeld.months}
                    onChange={(e) => onInputChange('licenseHeld', {
                      ...data.licenseHeld,
                      months: e.target.value
                    })}
                    className="w-full p-2 border rounded"
                    min="0"
                    max="11"
                    onWheel={(e) => e.currentTarget.blur()}
                    required={monthsVisible}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Do you own any other vehicles?
            </label>
            <select
              value={data.otherVehicles}
              onChange={(e) => onInputChange('otherVehicles', e.target.value)}
              className="w-full p-2 border rounded"
              required
            >
              <option value="">Select option</option>
              <option value="No">No</option>
              <option value="Own another vehicle">Own another vehicle</option>
              <option value="Named driver on another car">Named driver on another car</option>
              <option value="Company Car">Company Car</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}